import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../../../context/AuthContext";
import { ordersService } from "../../../services/ordersServices";
import "./orders.css";

function fmtMoney(x) {
  return Number(x ?? 0).toLocaleString(undefined, { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function toISODate(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function OrdersTab() {
  const { user } = useAuth(); // contient user.codtiers
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  // filtres date
  const today = new Date();
  const defaultFrom = new Date(today); defaultFrom.setMonth(today.getMonth() - 1); // 30 jours
  const [dateFrom, setDateFrom] = useState(toISODate(defaultFrom));
  const [dateTo, setDateTo] = useState(toISODate(today));

  // panneau de détails
  const [open, setOpen] = useState(false);
  const [header, setHeader] = useState(null);
  const [lines, setLines] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    if (!user?.codtiers) return;
    (async () => {
      setLoading(true);
      try {
        const list = await ordersService.listByClient(user.codtiers);
        setRows(list);
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.codtiers]);

  // applique filtre de dates côté client
  // src/component/Account/tabs/OrdersTab.jsx
  // ...
  const filtered = useMemo(() => {
    if (!rows?.length) return [];
    const from = dateFrom ? new Date(dateFrom + "T00:00:00") : null;
    const to = dateTo ? new Date(dateTo + "T23:59:59") : null;

    return rows.filter(r => {
      if (!r.date) return false; // ⬅️ exclure lignes sans date
      const t = r.date.getTime();
      if (from && t < from.getTime()) return false;
      if (to && t > to.getTime()) return false;
      return true;
    });
  }, [rows, dateFrom, dateTo]);
  // ...


  const openDetail = async (row) => {
    setOpen(true);
    setHeader(null);
    setLines([]);
    setLoadingDetail(true);

    try {
      const [h, ls] = await Promise.all([
        ordersService.getHeader(row.dbName, row.id),
        ordersService.getLines(row.dbName, row.id),
      ]);
      setHeader(h);
      setLines(ls);
    } finally {
      setLoadingDetail(false);
    }
  };


  return (
    <section className="acc-panel">
      <div className="acc-panelHeader">
        <h2 className="acc-title">Historique des commandes</h2>
        <div className="acc-filters">
          <label className="acc-filter">
            <span>Du</span>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </label>
          <label className="acc-filter">
            <span>Au</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </label>
        </div>
      </div>

      <div className="acc-table">
        <div className="acc-tr acc-thead">
          <div>N° commande</div>
          <div>Source</div>
          <div>Date</div>
          <div>Total TTC</div>
          <div>Action</div>
        </div>

        {loading
          ? Array.from({ length: 6 }).map((_, i) => (
            <div className="acc-tr" key={i}>
              <div className="skel skel-line" />
              <div className="skel skel-line" />
              <div className="skel skel-line" />
              <div className="skel skel-line" />
            </div>
          ))
          : filtered.map((r) => (
            <div className="acc-tr" key={`${r.dbName}-${r.nf}`}>
              <div>#{r.nf}</div>

              {/* ✅ NEW */}
              <div>
                <span className={`src-badge ${r.dbName === "Amen" ? "src-amen" : "src-mg"}`}>
                  {r.dbName === "Amen" ? "Amen" : "MG"}
                </span>
              </div>

              <div>{r.date ? r.date.toLocaleDateString() : "—"}</div>
              <div>{fmtMoney(r.total)} DT</div>

              <div>
                <button
                  className="acc-linkBtn"
                  disabled={!r?.id || !r?.dbName}
                  onClick={() => openDetail(r)}
                >
                  Détails
                </button>
              </div>
            </div>

          ))}

        {!loading && filtered.length === 0 && (
          <div className="acc-empty">Aucune commande trouvée pour la période.</div>
        )}
      </div>

      {/* --------- Modal de détails (portail body → au-dessus de tout) --------- */}
      {open && createPortal(
        <div className="order-modal" role="dialog" aria-modal="true">
          <div className="order-modal__backdrop" onClick={() => setOpen(false)} />
          <div className="order-modal__panel">
            <div className="order-modal__head">
              <h3>Commande #{header?.nf ?? "…"}</h3>
              <button className="order-modal__close" onClick={() => setOpen(false)}>✕</button>
            </div>

            {loadingDetail && <div className="order-modal__loading">Chargement…</div>}

            {!loadingDetail && header && (
              <>
                <div className="order-modal__grid">
                  <div className="order-info">
                    <div className="muted">Client</div>
                    <div className="order-info__v">{header.client}</div>
                  </div>
                  <div className="order-info">
                    <div className="muted">Adresse</div>
                    <div className="order-info__v">{header.adresse || "—"}</div>
                  </div>
                  <div className="order-info">
                    <div className="muted">Commercial</div>
                    <div className="order-info__v">{header.repres || "—"}</div>
                  </div>
                  <div className="order-info">
                    <div className="muted">Date</div>
                    <div className="order-info__v">{header.datUser ? header.datUser.toLocaleString() : "—"}</div>
                  </div>
                </div>
                <div className="order-remark">
                  <div className="muted">Remarque</div>
                  <div className="order-info__v">{header.remarq || "—"}</div>
                </div>

                <div className="order-totals">
                  <div><span>HT</span><b>{fmtMoney(header.totHT)} DT</b></div>
                  <div><span>TVA</span><b>{fmtMoney(header.totTVA)} DT</b></div>
                  <div><span>Remise</span><b>{fmtMoney(header.totRem)} DT</b></div>
                  <div className="emph"><span>Total TTC</span><b>{fmtMoney(header.totTTC)} DT</b></div>
                </div>

                <div className="order-lines">
                  <div className="order-lines__head">
                    <div>Code</div>
                    <div>Désignation</div>
                    <div>Qt</div>

                    <div>PU HT</div>
                    <div>PU TTC</div>
                    <div>Rem %</div>
                    <div>TVA %</div>
                  </div>
                  {lines.map((l, i) => (
                    <div className="order-lines__row" key={i}>
                      <div>{l.codArt}</div>
                      <div>{l.libArt}</div>

                      <div>{l.qt}</div>

                      <div>{fmtMoney(l.puht)}</div>
                      <div>{fmtMoney(l.puttc)}</div>
                      <div>{l.mntRem}</div>
                      <div>{l.mntTVA}</div>
                    </div>
                  ))}
                  {lines.length === 0 && (
                    <div className="order-lines__empty">Aucune ligne.</div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>,
        document.body
      )}

    </section>
  );
}
